import { Phone, Lightning, Crown, UserCircle } from "@phosphor-icons/react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";
import { LIFESTYLE_IMG } from "@/data/content";

const BENEFITS = [
  {
    I: Lightning,
    t: "Hidden fares",
    short: "10–30% below online",
    d: "Our travel desk has access to unpublished consolidator fares — often 10–30% below what you see online.",
  },
  {
    I: Crown,
    t: "Complex itineraries",
    short: "Multi-city · open-jaw",
    d: "Multi-city, open-jaw, group, or premium cabin redemptions — handled in one call, not three browser tabs.",
  },
  {
    I: UserCircle,
    t: "Real travel agent",
    short: "One person, end-to-end",
    d: "Speak with the same person from quote to ticket. No call centre script, no IVR maze.",
  },
];

export default function CallPromo() {
  return (
    <section
      className="bg-[#F7F5F0] py-10 md:py-20 px-5 md:px-12 lg:px-24"
      data-testid="call-promo-section"
    >
      {/* ============ MOBILE — compact: text over image + 3 USP cards ============ */}
      <div className="md:hidden">
        {/* Image with overlaid title + paragraph (replaces both the standalone
            image block and the heading/paragraph that used to follow it) */}
        <div className="relative overflow-hidden aspect-[5/6]">
          <img
            src={LIFESTYLE_IMG}
            alt="Speaking with a travel specialist"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/85" />
          <div className="absolute inset-0 p-5 flex flex-col justify-between text-[#F7F5F0]">
            <div>
              <p className="tcp-overline text-[#C29B62]">Better When You Call</p>
              <h2 className="font-serif text-3xl tracking-tighter leading-tight mt-2">
                The best fares <em className="italic" style={{ color: "#C29B62" }}>aren't online.</em>
              </h2>
              <p className="text-[#D7D5D0] text-sm leading-relaxed mt-3 max-w-[90%]">
                Premium cabin upgrades, complex routings, and group bookings
                are nearly always cheaper — and faster — over the phone.
              </p>
            </div>
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="tcp-overline text-[#C29B62] text-[10px]">Average call</p>
                <p className="font-serif text-xl tracking-tight">Under 4 minutes to a quote</p>
              </div>
            </div>
          </div>
        </div>

        {/* USPs — compact 3-card tabular row */}
        <div
          className="mt-5 grid grid-cols-3 gap-2 border border-[#E0DDD6] bg-white"
          data-testid="call-benefits-mobile"
        >
          {BENEFITS.map(({ I, t, short }, i) => (
            <div
              key={i}
              className={`p-3 text-center ${
                i < BENEFITS.length - 1 ? "border-r border-[#E0DDD6]" : ""
              }`}
              data-testid={`call-benefit-mobile-${i}`}
            >
              <div className="w-9 h-9 mx-auto bg-[#0A0A0B] text-[#C29B62] flex items-center justify-center">
                <I size={16} weight="duotone" />
              </div>
              <p className="text-[12px] font-semibold text-[#1C1C1C] mt-2 leading-tight">{t}</p>
              <p className="text-[10px] text-[#666460] mt-1 leading-tight">{short}</p>
            </div>
          ))}
        </div>

        {/* Single big phone CTA */}
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="call-promo-cta-mobile"
          className="mt-5 w-full bg-[#C29B62] hover:bg-[#D4AC75] text-[#0A0A0B] font-semibold text-sm tracking-[0.18em] uppercase transition-colors flex items-center justify-center gap-2 py-3.5"
        >
          <Phone size={16} weight="fill" /> Call {PHONE_DISPLAY}
        </a>
        <p className="text-[11px] text-[#666460] mt-2 text-center">
          Toll-free across the US · No hold music, real people
        </p>
      </div>

      {/* ============ DESKTOP — unchanged ============ */}
      <div className="hidden md:grid grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <p className="tcp-overline text-[#C29B62] mb-3">Better When You Call</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#1C1C1C] mb-4">
            The best fares <br /> aren't <em className="italic">online.</em>
          </h2>
          <p className="text-[#666460] text-base leading-relaxed mb-6 max-w-md">
            Sites like ours show you a great fare. Calling us gets you the great one.
            Premium cabin upgrades, multi-city routings, group bookings and one-off
            destinations are nearly always cheaper — and faster — over the phone.
          </p>

          <ul className="space-y-3.5 mb-7">
            {BENEFITS.map(({ I, t, d }, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`call-benefit-${i}`}>
                <div className="w-10 h-10 bg-[#0A0A0B] text-[#C29B62] flex items-center justify-center shrink-0">
                  <I size={20} weight="duotone" />
                </div>
                <div>
                  <p className="font-semibold text-[#1C1C1C]">{t}</p>
                  <p className="text-sm text-[#666460] leading-relaxed">{d}</p>
                </div>
              </li>
            ))}
          </ul>

          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="call-promo-cta"
            className="btn-gold inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase"
          >
            <Phone size={16} weight="fill" /> Call {PHONE_DISPLAY}
          </a>
          <p className="text-xs text-[#666460] mt-3">Toll-free across the United States · No hold music, real people</p>
        </div>

        <div className="md:col-span-6 order-1 md:order-2 relative aspect-[5/4] overflow-hidden">
          <img src={LIFESTYLE_IMG} alt="Speaking with a travel specialist" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-[#F7F5F0]">
            <p className="tcp-overline text-[#C29B62]">Average call</p>
            <p className="font-serif text-2xl tracking-tight">Under 4 minutes to a quote.</p>
            <p className="text-sm text-[#A09E9A] mt-1">Open every day · US-based team</p>
          </div>
        </div>
      </div>
    </section>
  );
}
